import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Check, ArrowRight, ArrowLeft } from "lucide-react";
import PageHero from "../components/PageHero";
import { stages } from "../mock/pages";
import { Eyebrow } from "../components/SectionHeading";
import useSeo from "../hooks/useSeo";

const order = ["pre-kg-upper-kg", "grades-1-2", "grades-3-7"];

export default function StagePage() {
  const { slug } = useParams();
  const stage = stages[slug];

  useSeo({
    title: stage ? `${stage.title} — ${stage.badge}` : "Academics",
    path: `/academics/${slug}`,
    description: stage
      ? `${stage.title} at The KPR Academy, Coimbatore: ${stage.intro}`
      : "Stage-wise CBSE curriculum at The KPR Academy, Coimbatore.",
    keywords: `${stage ? stage.title : "academics"} CBSE Coimbatore, KPR Academy ${
      stage ? stage.badge : "curriculum"
    }, school subjects assessment Coimbatore`,
  });

  if (!stage) return <Navigate to="/academics" replace />;

  const idx = order.indexOf(slug);
  const prev = idx > 0 ? order[idx - 1] : null;
  const next = idx < order.length - 1 ? order[idx + 1] : null;

  return (
    <>
      <PageHero
        eyebrow={stage.badge}
        title={stage.title}
        sub={stage.intro}
        crumbs={[{ label: "Academics", to: "/academics" }, { label: stage.title }]}
        image={stage.image}
      />

      <section className="py-24 lg:py-28" style={{ background: "var(--cream)" }}>
        <div className="mx-auto grid max-w-[1280px] gap-14 px-5 lg:grid-cols-[1.1fr_0.9fr] lg:gap-20 lg:px-6">
          <div data-reveal="">
            <Eyebrow>What the day looks like</Eyebrow>
            <div className="mt-8 space-y-4">
              {stage.highlights.map((h) => (
                <div
                  key={h.title}
                  className="rounded-2xl bg-white p-6"
                  style={{ border: "1px solid rgba(11,59,54,.09)", transition: "box-shadow .3s ease" }}
                  onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 26px 50px -36px rgba(8,46,41,.5)")}
                  onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}
                >
                  <h3 className="font-display text-[1.15rem]" style={{ color: "var(--teal-900)" }}>
                    {h.title}
                  </h3>
                  <p className="mt-2 text-[0.89rem] leading-relaxed" style={{ color: "var(--muted-ink)" }}>
                    {h.text}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div data-reveal="" style={{ transitionDelay: "120ms" }}>
            <div className="overflow-hidden rounded-2xl">
              <img src={stage.image} alt={stage.title} className="h-[260px] w-full object-cover" />
            </div>

            <div className="mt-8 rounded-2xl p-7" style={{ background: "var(--sand)" }}>
              <Eyebrow>Subjects & activities</Eyebrow>
              <div className="mt-5 flex flex-wrap gap-2.5">
                {stage.subjects.map((s) => (
                  <span
                    key={s}
                    className="flex items-center gap-2 rounded-full bg-white px-4 py-2 text-[0.8rem] font-medium"
                    style={{ color: "var(--teal-800)", border: "1px solid rgba(11,59,54,.1)" }}
                  >
                    <Check size={13} strokeWidth={3} style={{ color: "var(--gold)" }} />
                    {s}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-6 rounded-2xl p-7" style={{ background: "var(--teal-900)" }}>
              <Eyebrow tone="light">Assessment</Eyebrow>
              <p className="mt-4 text-[0.94rem] leading-relaxed" style={{ color: "rgba(251,247,240,.82)" }}>
                {stage.assessment}
              </p>
            </div>
          </div>
        </div>

        <div className="mx-auto mt-16 flex max-w-[1280px] flex-wrap items-center justify-between gap-4 px-5 lg:px-6">
          {prev ? (
            <Link
              to={`/academics/${prev}`}
              className="group flex items-center gap-3 text-[0.86rem] font-semibold"
              style={{ color: "var(--teal-800)" }}
            >
              <ArrowLeft size={16} className="group-hover:-translate-x-1" style={{ transition: "transform .3s ease" }} />
              {stages[prev].title}
            </Link>
          ) : (
            <span />
          )}
          {next && (
            <Link
              to={`/academics/${next}`}
              className="group flex items-center gap-3 text-[0.86rem] font-semibold"
              style={{ color: "var(--teal-800)" }}
            >
              {stages[next].title}
              <ArrowRight size={16} className="group-hover:translate-x-1" style={{ transition: "transform .3s ease" }} />
            </Link>
          )}
        </div>
      </section>
    </>
  );
}
